import { getCategoryCanonicalUrlV2 } from "api/category/getCategoryCanonicalUrlV2";
import LoaderSkeleton from "component/loader-skeleton/LoaderSkeleton";
import PartNumberPage from "modules/part/PartNumberPage";
import ShoppingCartShare from "modules/shopping-cart/ShoppingCartShare";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { wrapper } from "store";
import nextI18nextConfig from "../../next-i18next.config";
import { getProductBySkuApi } from "../api/product/getProductBySkuApi";
import { validateVehicle } from "../util/vehicle/validate-vehicle";

const Validate = dynamic(() => import(/* webpackChunkName: "validate" */"modules/accounts/validate.container"), {
  ssr: false,
});

const NotFound = dynamic(() => import(/* webpackChunkName: "notfound" */"component/notfound/notfound.component"), {
  ssr: false,
});

const Restore = dynamic(() => import(/* webpackChunkName: "order-restore" */ "container/order/order-restore.container"), {
  ssr: false,
});

const ResetPassword = dynamic(() => import(/* webpackChunkName: "reset-password" */"modules/accounts/reset-password.container"), {
  ssr: false,
});

const InVoice = dynamic(() => import(/* webpackChunkName: "invoice" */"modules/invoice"), {
  ssr: false,
});

const OrderVerification = dynamic(() => import(/* webpackChunkName: "order-verification" */"container/order-verification/OrderVerification"), {
  ssr: false,
});

const PickupOfferAfterOrder = dynamic(() => import(/* webpackChunkName: "pickup-offer-after-order" */"modules/pickup-offer/PickupOfferAfterOrder"), {
  ssr: false,
});

const RefundLookupContainer = dynamic(() => import(/* webpackChunkName: "refund-lookup" */"modules/accounts/refund-lookup/RefundLookupContainer"), {
  ssr: false,
});

const MAPPING = {
  "replacement-parts": "/replacement-parts",
  replacement_parts: "/replacement-parts",
  "body-parts": "/automotive-body-parts",
  body_parts: "/automotive-body-parts",
  "automotive-lightings": "/automotive-lightings",
  automotive_lightings: "/automotive-lightings",
  "tools-&-equipment": "/tools-and-equipment",
  "tools_&_equipment": "/tools-and-equipment",
  "performance-parts": "/performance-parts",
  "wheels-&-tires": "/wheels",
  "wheels-and-tires": "/wheels",
  exterior: "/exterior",
  interior: "/interior",
  "specialty-parts": "/specialty-parts",
};

const TIER0_MAPPING = {
  "replacement-parts": "replacement parts",
  replacement_parts: "replacement parts",
  "body-parts": "body parts",
  "automotive-lightings": "automotive lightings",
  "tools-&-equipment": "tools & equipment",
  "tools_&_equipment": "tools & equipment",
  "performance-parts": "performance parts",
  "wheels-&-tires": "wheels",
  "wheels-and-tires": "wheels",
  exterior: "exterior",
  interior: "interior",
  "speciality-parts": "specialty parts",
};

function Slug(props = {}) {
  const { action, redirectUrl } = props;
  const router = useRouter();

  const [feature, uuid, , secondUuid] = router.query.slug || [];

  useEffect(() => {
    if (feature === "blog") {
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    if (action === "REDIRECT") {
      router.push(redirectUrl);
    }
  }, [redirectUrl]);

  if (action === "REDIRECT") {
    return <LoaderSkeleton count={20} height={100} />;
  }

  if (feature === "support" && uuid === "refunds") {
    return <RefundLookupContainer />;
  }

  if (feature === "blog") {
    return null;
  }

  if (feature === "validate") {
    return <Validate {...{ uuid }} />;
  }
  if (feature === "share") {
    return <ShoppingCartShare {...{ uuid }} />;
  }
  if (feature === "part") {
    return <PartNumberPage {...{ sku: uuid }} />;
  }
  if (feature === "restore") {
    return <Restore {...{ uuid }} />;
  }
  if (feature === "reorder") {
    return <Restore {...{ orderId: uuid }} />;
  }
  if (feature === "reset-password") {
    return <ResetPassword {...{ uuid }} />;
  }
  if (feature === "invoice" || (feature === "support" && secondUuid)) {
    return <InVoice {...{ uuid, secondUuid, feature }} />;
  }
  if (feature === "order-verification") {
    return <OrderVerification />;
  }
  if (feature === "modify-order-shipment") {
    return <PickupOfferAfterOrder {...{ uuid }} />;
  }

  return <NotFound />;
}

// Todo: Revisit this. There might be bug here as we are using router
export const getServerSideProps = wrapper.getServerSideProps((store) => async ({ query, res, locale }) => {
  // eslint-disable-next-line no-unused-vars
  const [feature, tier0, sku] = query.slug;
  let props = {};
  const locales = ["common", "category-map", "account"];

  if (feature === "blog") {
    return null;
  }
  if (feature === "modify-order-shipment") {
    locales.push("checkout");
  }
  if (feature === "part" || feature === "invoice") {
    locales.push("invoice");
    // if (feature === "part") {
    //   locales.push("product");
    // } else if (feature === "invoice") {
    //   locales.push("invoice");
    // }
  }
  props = await serverSideTranslations(locale, locales, nextI18nextConfig);

  // redirect such URLs
  if (["www.partsavatar.ca", "partsavatar.ca"].includes(tier0)) {
    // /ngk-usa-v-phttps://partsavatar.ca/ngk-usa-v-power-spark-plug-pack-of-4-6376ower-spark-plug-pack-of-4
    // /all-makes,%20https://www.partsavatar.ca/autoparts-purchase-sale-terms,%20https://www.partsavatar.ca/articles/ultimate-buying-guides
    // /https://partsavatar.ca/heater-valve?manufacturers=COOLING%20DEPOT&manufacturers=FOUR%20SEASONS
    if (res && res.writeHead && !res.headersSent) {
      res.setHeader("cache-control", "max-age=0");
      res.writeHead(301, {
        Location: "/",
      });
      res.end();
      return { props };
    }
  }

  if (feature === "product") {
    // https://partsavatar.ca/product/2ny466abdn
    // https://partsavatar.ca/product/dorman--oe-solutions--air-intake-hose-696-003/nm4rmjkoxn

    const response = await getProductBySkuApi(!sku ? tier0 : sku);
    const { canonicalUrl } = response.data.product;
    const additionalProps = {
      action: "REDIRECT",
      redirectUrl: canonicalUrl,
    };

    return {
      props: {
        ...props,
        ...additionalProps,
      },
    };
  }

  if (feature === "share") {
    if (res && !res.headersSent) {
      res.setHeader("cache-control", "max-age=0");
    }
    return { props };
  }

  if (feature !== "category") {
    return { props };
  }

  const {
    t1, t2, t3, make, model, year,
  } = query;
  let redirectUrl = MAPPING[tier0] || "/";
  const additionalProps = {};
  try {
    const reduxState = store.getState();
    const { selectedVehicle } = reduxState.garage;
    const categoryObj = {
      tier0: TIER0_MAPPING[tier0], tier1: t1, tier2: t2, tier3: t3,
    };
    let vehicleInfo = { make, model, year };
    if (validateVehicle(selectedVehicle)) {
      vehicleInfo = selectedVehicle;
    }
    if (categoryObj.tier0) {
      const urlRes = await getCategoryCanonicalUrlV2(categoryObj, vehicleInfo);
      const { url } = urlRes.data;
      redirectUrl = url || MAPPING[tier0] || "/";
    }
  } finally {
    if (res && res.writeHead && !res.headersSent) {
      // don't cache, as t1, t2, t3 are not in whitelist params
      // check issue for more details https://www.wrike.com/open.htm?id=628014008
      res.setHeader("cache-control", "max-age=0");
      res.writeHead(301, {
        Location: redirectUrl,
      });
      res.end();
    } else {
      additionalProps.action = "REDIRECT";
      additionalProps.redirectUrl = redirectUrl;
    }
  }
  return {
    props: {
      ...props,
      ...additionalProps,
    },
  };
});

export default Slug;
